import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useContext } from "react";
import ReactDOM from "react-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";

/**
 * PieceLookup — bidirectional SKU ↔ description search with catalog dropdown.
 *
 * Props:
 *   value         { pieceId, sku, description, price, source } | null
 *   onChange      (value) => void
 *   placeholder   string
 *   disabled      boolean
 *   required      boolean
 *
 * value.source:
 *   "catalog"   — linked to an existing Piece document (pieceId set)
 *   "temp"      — ad-hoc item, no pieceId
 *   "extracted" — pre-filled by AI from PDF, pending user review (associate or register new)
 *   null        — empty / not yet selected
 *
 * The dropdown is rendered via React Portal into document.body (position: fixed)
 * so it escapes overflow:hidden and border-collapse clipping in table contexts.
 */
const PieceLookup = ({
  value,
  onChange,
  placeholder = "SKU o descripción...",
  disabled = false,
  required = false,
  name,
}) => {
  const [query, setQuery] = useState(value?.description || value?.sku || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [promoting, setPromoting] = useState(false);
  const [promoteError, setPromoteError] = useState("");

  // 28-ago (auditoría roles) — el quick-create de pieza (POST /piece) es de
  // admin/operator: al resto (seller) no se le ofrece la opción "Registrar en
  // catálogo…" (le queda "Usar solo para esta OC" como ítem temporal).
  const auth = useContext(AuthContext);
  const userRoles = auth?.user?.roles || [];
  const puedeCrearPieza = ["admin", "operator"].some(r => userRoles.includes(r));

  // Modal "registrar pieza nueva" con campos extendidos
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCorte, setShowCorte] = useState(false);
  const emptyForm = {
    sku: "", name: "", price: "", material: "", geometry: "", details: "",
    requiereDosier: false, esImportada: false,
    managedStock: false, reorderPoint: "", consumoPorUnidad: "",
    requiereCorteExterno: false, codigoCorte: "", precioCorte: "",
    // Catálogo temporal — pieza con auto-expiración a 90 días para
    // cotizaciones de clientes ocasionales. Default: permanente (false).
    temporary: false,
  };
  const [form, setForm] = useState(emptyForm);
  const setF = (patch) => setForm(prev => ({ ...prev, ...patch }));

  // Portal positioning
  const [dropdownRect, setDropdownRect] = useState(null);

  const timerRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Sync display query when value changes externally
  useEffect(() => {
    if (value?.source === "catalog" || value?.source === "temp" || value?.source === "extracted") {
      const display = value.sku
        ? `${value.sku} — ${value.description}`
        : value.description || "";
      setQuery(display);
    } else if (!value) {
      setQuery("");
    }
  }, [value]);

  // Compute dropdown position immediately after open state changes
  useLayoutEffect(() => {
    if (open && inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 2, left: r.left, width: r.width });
    }
  }, [open]);

  // Reposition on scroll or resize while dropdown is open
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (inputRef.current) {
        const r = inputRef.current.getBoundingClientRect();
        setDropdownRect({ top: r.bottom + 2, left: r.left, width: r.width });
      }
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  // Close dropdown on outside click (must check both container and portal dropdown)
  useEffect(() => {
    const handler = e => {
      const insideContainer = containerRef.current?.contains(e.target);
      const insideDropdown  = dropdownRef.current?.contains(e.target);
      if (!insideContainer && !insideDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = useCallback(async q => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await backend.get(`piece/search?q=${encodeURIComponent(q.trim())}`);
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = e => {
    const q = e.target.value;
    setQuery(q);
    setOpen(true);
    setActiveIndex(-1);
    onChange?.(null); // clear selection while typing
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(q), 200);
  };

  const selectCatalog = piece => {
    onChange?.({
      pieceId: piece._id,
      sku: piece.sku || "",
      description: piece.name,
      price: piece.price || 0,
      source: "catalog",
      temporary: !!piece.temporary,
      expiresAt: piece.expiresAt || null,
    });
    setOpen(false);
    setResults([]);
  };

  const promoteSelected = async () => {
    if (!value?.pieceId || !value?.temporary) return;
    setPromoting(true);
    setPromoteError("");
    try {
      const { data } = await backend.patch(`piece/${value.pieceId}/promote`);
      onChange?.({
        ...value,
        temporary: false,
        expiresAt: null,
        sku: data?.sku ?? value.sku,
        description: data?.name ?? value.description,
      });
    } catch (err) {
      setPromoteError(err?.response?.data?.error || "No se pudo promover la pieza");
    } finally {
      setPromoting(false);
    }
  };

  const selectTemp = () => {
    onChange?.({
      pieceId: null,
      sku: "",
      description: query,
      price: 0,
      source: "temp",
    });
    setOpen(false);
    setResults([]);
  };

  const openCreateModal = () => {
    // Pre-llenar con el query actual (puede traer "SKU — desc" si venía de extracted)
    let presetSku = "";
    let presetName = query || "";
    const m = /^([^—-]+?)\s*[—-]\s*(.+)$/.exec(query || "");
    if (m) { presetSku = m[1].trim(); presetName = m[2].trim(); }
    setForm({ ...emptyForm, sku: value?.sku || presetSku, name: value?.description || presetName, price: String(value?.price || "") });
    setShowAdvanced(false);
    setShowCorte(false);
    setCreateError("");
    setOpen(false);
    setCreateOpen(true);
  };

  const handleCreateCatalog = async () => {
    setCreateError("");
    if (!form.name.trim()) { setCreateError("La descripción es obligatoria"); return; }
    setCreating(true);
    try {
      const payload = {
        name: form.name.trim(),
        sku: form.sku.trim() || undefined,
        price: Number(form.price) || 0,
        processes: [],
      };
      if (form.material.trim())  payload.material  = form.material.trim();
      if (form.geometry.trim())  payload.geometry  = form.geometry.trim();
      if (form.details.trim())   payload.details   = form.details.trim();
      if (form.requiereDosier)   payload.requiereDosier = true;
      if (form.esImportada)      payload.esImportada = true;
      if (form.temporary)        payload.temporary = true;
      if (form.managedStock) {
        payload.managedStock = true;
        if (form.reorderPoint !== "")     payload.reorderPoint = Number(form.reorderPoint) || 0;
        if (form.consumoPorUnidad !== "") payload.consumoPorUnidad = Number(form.consumoPorUnidad) || 1;
      }
      if (form.requiereCorteExterno) {
        payload.requiereCorteExterno = true;
        if (form.codigoCorte.trim()) payload.codigoCorte = form.codigoCorte.trim();
        if (form.precioCorte !== "") payload.precioCorte = Number(form.precioCorte) || 0;
      }
      const res = await backend.post("piece", payload);
      const piece = res.data;
      selectCatalog(piece);
      setCreateOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data || err.message || "Error al crear pieza";
      setCreateError(typeof msg === "string" ? msg : "Error al crear pieza");
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = e => {
    if (!open) return;
    // results + "temp" (+ "create" solo si el rol puede crear piezas)
    const total = results.length + (puedeCrearPieza ? 2 : 1);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, total - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < results.length) {
        selectCatalog(results[activeIndex]);
      } else if (activeIndex === results.length) {
        selectTemp();
      } else if (activeIndex === results.length + 1 && puedeCrearPieza) {
        openCreateModal();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const inputClass = [
    "bini-input",
    value?.source === "catalog" ? "has-match" : "",
    value?.source === "temp" ? "is-temp" : "",
    value?.source === "extracted" ? "is-extracted" : "",
    required && !value ? "is-error" : "",
  ].filter(Boolean).join(" ");

  const dropdown = open && query.length >= 2 && dropdownRect
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="pl-dropdown"
          style={{
            position: "fixed",
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: dropdownRect.width,
            right: "auto",
            marginTop: 0,
            zIndex: 9999,
          }}
        >
          {loading && <div className="pl-empty">Buscando...</div>}

          {!loading && results.length === 0 && (
            <div className="pl-empty">Sin coincidencias para "{query}"</div>
          )}

          {results.map((piece, i) => (
            <div
              key={piece._id}
              className={`pl-item ${activeIndex === i ? "active" : ""}`}
              onMouseDown={() => selectCatalog(piece)}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {piece.sku && <span className="pl-sku">{piece.sku}</span>}
              <span className="pl-desc">{piece.name}</span>
              {piece.temporary && <span style={{ color: "var(--bini-amber)", fontSize: 10 }}>Temporal</span>}
              {piece.price > 0 && (
                <span className="pl-price">
                  ${piece.price.toLocaleString("es-CL")}
                </span>
              )}
            </div>
          ))}

          {/* Temporal fallback + create in catalog */}
          {!loading && (
            <div className="pl-create-zone">
              <div
                className={`pl-item ${activeIndex === results.length ? "active" : ""}`}
                onMouseDown={selectTemp}
                onMouseEnter={() => setActiveIndex(results.length)}
              >
                <span style={{ color: "var(--bini-amber)" }}>⚡</span>
                <span className="pl-desc">Usar "{query}" solo para esta OC</span>
              </div>
              {puedeCrearPieza && (
              <div
                className={`pl-item ${activeIndex === results.length + 1 ? "active" : ""}`}
                onMouseDown={e => { e.preventDefault(); openCreateModal(); }}
                onMouseEnter={() => setActiveIndex(results.length + 1)}
              >
                <span style={{ color: "var(--bini-blue)" }}>⊕</span>
                <span className="pl-desc">Registrar "{query}" en catálogo…</span>
              </div>
              )}
            </div>
          )}
        </div>,
        document.body
      )
    : null;

  // ── Modal "registrar pieza nueva" ────────────────────────────────────────
  const createModal = createOpen
    ? ReactDOM.createPortal(
        <div
          onMouseDown={e => { if (e.target === e.currentTarget) setCreateOpen(false); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              background: "#1a1a1a", border: "1px solid #333", borderRadius: 8,
              width: "min(560px, 92vw)", maxHeight: "88vh", overflowY: "auto",
              padding: "18px 20px", color: "#ddd", fontSize: 12,
              fontFamily: "Arial, sans-serif",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>
                ⊕ Registrar pieza en catálogo
              </div>
              <button
                onClick={() => setCreateOpen(false)}
                style={{
                  background: "transparent", border: "none", color: "#cbd5e1",
                  fontSize: 18, cursor: "pointer", lineHeight: 1,
                }}
                title="Cerrar"
              >×</button>
            </div>

            {/* Selector permanente vs temporal — al inicio para que sea
                la primera decisión y se vea claro qué tipo de catálogo. */}
            <div style={{
              display: "flex", gap: 6, marginBottom: 14,
              background: "#161616", border: "1px solid #2e2e2e",
              borderRadius: 6, padding: 4,
            }}>
              <button
                type="button"
                onClick={() => setF({ temporary: false })}
                style={{
                  flex: 1, padding: "8px 12px", fontSize: 12, fontWeight: 600,
                  background: !form.temporary ? "#2ecc7122" : "transparent",
                  color: !form.temporary ? "#2ecc71" : "#888",
                  border: !form.temporary ? "1px solid #2ecc7155" : "1px solid transparent",
                  borderRadius: 4, cursor: "pointer", textAlign: "left",
                }}
              >
                ⊙ Permanente
                <div style={{ fontSize: 10, fontWeight: 400, color: "#aaa", marginTop: 2 }}>
                  Catálogo principal · permanece indefinidamente
                </div>
              </button>
              <button
                type="button"
                onClick={() => setF({ temporary: true })}
                style={{
                  flex: 1, padding: "8px 12px", fontSize: 12, fontWeight: 600,
                  background: form.temporary ? "#f39c1222" : "transparent",
                  color: form.temporary ? "#f39c12" : "#888",
                  border: form.temporary ? "1px solid #f39c1255" : "1px solid transparent",
                  borderRadius: 4, cursor: "pointer", textAlign: "left",
                }}
              >
                ⏱ Temporal · 90 días
                <div style={{ fontSize: 10, fontWeight: 400, color: "#aaa", marginTop: 2 }}>
                  Para cliente ocasional · auto-archive en 90 días
                </div>
              </button>
            </div>

            {/* Bloque básicos */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <Field label="SKU (opcional)">
                <input className="bini-input bini-mono" value={form.sku}
                  onChange={e => setF({ sku: e.target.value })} autoFocus />
              </Field>
              <Field label="Precio unitario">
                <input className="bini-input" type="number" min="0" value={form.price}
                  onChange={e => setF({ price: e.target.value })} />
              </Field>
              <Field label="Descripción *" full>
                <input className="bini-input" value={form.name}
                  onChange={e => setF({ name: e.target.value })} />
              </Field>
              <Field label="Material">
                <input className="bini-input" value={form.material}
                  onChange={e => setF({ material: e.target.value })}
                  placeholder="ej: Acero A36" />
              </Field>
              <Field label="Geometría / forma">
                <input className="bini-input" value={form.geometry}
                  onChange={e => setF({ geometry: e.target.value })}
                  placeholder="ej: Plancha 6mm" />
              </Field>
              <Field label="Detalles / notas" full>
                <input className="bini-input" value={form.details}
                  onChange={e => setF({ details: e.target.value })} />
              </Field>
            </div>

            {/* Bloque avanzado (colapsable) */}
            <CollapseHeader open={showAdvanced} onToggle={() => setShowAdvanced(s => !s)}>
              Opciones avanzadas (calidad / stock)
            </CollapseHeader>
            {showAdvanced && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "8px 0 14px" }}>
                <CheckRow checked={form.requiereDosier} onChange={v => setF({ requiereDosier: v })}
                  label="Requiere dossier de calidad (estructural/crítica)" full />
                <CheckRow checked={form.esImportada} onChange={v => setF({ esImportada: v })}
                  label="Es pieza importada (comprada hecha, sin control dim)" full />
                <CheckRow checked={form.managedStock} onChange={v => setF({ managedStock: v })}
                  label="Gestionar stock (aparece en /inventario)" full />
                {form.managedStock && (
                  <>
                    <Field label="Punto de reorden">
                      <input className="bini-input" type="number" min="0" value={form.reorderPoint}
                        onChange={e => setF({ reorderPoint: e.target.value })} />
                    </Field>
                    <Field label="Consumo por unidad">
                      <input className="bini-input" type="number" min="0" step="0.01" value={form.consumoPorUnidad}
                        onChange={e => setF({ consumoPorUnidad: e.target.value })}
                        placeholder="1" />
                    </Field>
                  </>
                )}
              </div>
            )}

            {/* Bloque corte externo (colapsable) */}
            <CollapseHeader open={showCorte} onToggle={() => setShowCorte(s => !s)}>
              Corte externo (proveedor láser/plasma)
            </CollapseHeader>
            {showCorte && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: "8px 0 14px" }}>
                <CheckRow checked={form.requiereCorteExterno} onChange={v => setF({ requiereCorteExterno: v })}
                  label="Se manda a cortar afuera" full />
                {form.requiereCorteExterno && (
                  <>
                    <Field label="Código de corte (interno)">
                      <input className="bini-input bini-mono" value={form.codigoCorte}
                        onChange={e => setF({ codigoCorte: e.target.value })}
                        placeholder="ej: BIN-CRT-042" />
                    </Field>
                    <Field label="Precio acordado por unidad">
                      <input className="bini-input" type="number" min="0" value={form.precioCorte}
                        onChange={e => setF({ precioCorte: e.target.value })} />
                    </Field>
                    <div style={{ gridColumn: "1 / -1", fontSize: 10, color: "#cbd5e1", fontStyle: "italic" }}>
                      Proveedor y plano se asignan después en /catalogo.
                    </div>
                  </>
                )}
              </div>
            )}

            {createError && (
              <div style={{
                color: "var(--bini-error, #e74c3c)", fontSize: 11, marginTop: 4,
                background: "rgba(231,76,60,0.08)", padding: "6px 10px", borderRadius: 4,
                border: "1px solid rgba(231,76,60,0.3)",
              }}>
                {createError}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button
                onClick={() => setCreateOpen(false)}
                disabled={creating}
                style={{
                  background: "transparent", border: "1px solid #444",
                  color: "#aaa", borderRadius: 5, padding: "7px 14px",
                  fontSize: 12, cursor: "pointer",
                }}
              >Cancelar</button>
              <button
                onClick={handleCreateCatalog}
                disabled={creating}
                style={{
                  background: "#3498db", border: "none", color: "#fff",
                  borderRadius: 5, padding: "7px 16px", fontSize: 12,
                  fontWeight: 600, cursor: creating ? "wait" : "pointer",
                  opacity: creating ? 0.7 : 1,
                }}
              >{creating ? "Guardando…" : "Registrar y seleccionar"}</button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <input
        ref={inputRef}
        className={inputClass}
        name={name}
        value={query}
        onChange={handleInputChange}
        onFocus={() => query.length >= 2 && setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {value?.source === "catalog" && value?.temporary && (
        <div style={{
          marginTop: 4, padding: "6px 8px", borderRadius: 4,
          border: "1px solid rgba(243,156,18,.4)", background: "rgba(243,156,18,.08)",
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 10,
        }}>
          <span style={{ color: "var(--bini-amber)", flex: 1 }}>
            ⏱ Pieza temporal: expira {value.expiresAt ? new Date(value.expiresAt).toLocaleDateString("es-CL") : "90 días después de creada"}.
          </span>
          {puedeCrearPieza && (
            <button type="button" className="bini-btn" disabled={promoting} onClick={promoteSelected}
              style={{ padding: "3px 8px", fontSize: 10 }}>
              {promoting ? "Promoviendo…" : "Promover al catálogo"}
            </button>
          )}
          {promoteError && <span style={{ color: "var(--bini-error, #e74c3c)", width: "100%" }}>{promoteError}</span>}
        </div>
      )}
      {dropdown}
      {createModal}
    </div>
  );
};

// ── helpers de presentación del modal ──────────────────────────────────────
const Field = ({ label, full, children }) => (
  <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
    <div style={{
      fontSize: 9.5, fontWeight: 700, color: "#cbd5e1",
      textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3,
    }}>{label}</div>
    {children}
  </div>
);

const CheckRow = ({ checked, onChange, label, full }) => (
  <label style={{
    gridColumn: full ? "1 / -1" : "auto",
    display: "flex", alignItems: "center", gap: 8,
    fontSize: 11, color: "#ccc", cursor: "pointer", padding: "4px 0",
  }}>
    <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
    {label}
  </label>
);

const CollapseHeader = ({ open, onToggle, children }) => (
  <div
    onClick={onToggle}
    style={{
      cursor: "pointer", padding: "6px 8px", fontSize: 11,
      fontWeight: 600, color: "#aaa", background: "#222",
      border: "1px solid #2e2e2e", borderRadius: 4, marginTop: 6,
      display: "flex", alignItems: "center", gap: 6, userSelect: "none",
    }}
  >
    <span style={{ color: "#3498db" }}>{open ? "▼" : "▶"}</span>
    {children}
  </div>
);

export default PieceLookup;
